import React, { useState, useEffect } from 'react';
import { Close as CloseIcon } from '@mui/icons-material';
import { CompetitorWeakness, CompetitorCategory } from '../types/competitor';
import { Button } from './ui/Button';
import { Dropdown } from './ui/Dropdown';

interface WeaknessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (weakness: Partial<CompetitorWeakness>) => void;
  weakness?: CompetitorWeakness | null;
  title?: string;
}

const WeaknessModal: React.FC<WeaknessModalProps> = ({
  isOpen,
  onClose,
  onSave,
  weakness,
  title = 'Add Weakness'
}) => {
  const [formData, setFormData] = useState<Partial<CompetitorWeakness>>({
    point: '',
    category: CompetitorCategory.OTHER
  });

  useEffect(() => {
    if (weakness) {
      setFormData({
        point: weakness.point || '',
        category: weakness.category || CompetitorCategory.OTHER
      });
    } else {
      setFormData({
        point: '',
        category: CompetitorCategory.OTHER
      });
    }
  }, [weakness]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.point?.trim()) {
      onSave(formData);
      onClose();
    }
  };

  const getCategoryLabel = (category: CompetitorCategory): string => {
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop">
      <div className="p-6 w-full max-w-md modal-panel">
        <div className="flex justify-between items-center mb-4">
          <h2 className="heading-3">{title}</h2>
          <Button variant="tertiary" size="sm" onClick={onClose} className="!p-1">
            <CloseIcon />
          </Button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink-secondary mb-1">
                Weakness Point *
              </label>
              <textarea
                value={formData.point}
                onChange={(e) => setFormData({ ...formData, point: e.target.value })}
                className="w-full px-3 py-2 border border-ink-border rounded-md focus:outline-none focus:ring-2 focus:ring-signature"
                rows={3}
                required
                placeholder="Describe the competitor's weakness..."
              />
            </div>

            <div>
              <label id="weakness-category-label" className="block text-sm font-medium text-ink-secondary mb-1">
                Category *
              </label>
              <Dropdown
                labelledBy="weakness-category-label"
                width="field"
                required
                value={formData.category ?? CompetitorCategory.OTHER}
                onChange={(category) => setFormData({ ...formData, category: category as CompetitorCategory })}
                options={Object.values(CompetitorCategory).map((cat) => ({ value: cat, label: getCategoryLabel(cat) }))}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={!formData.point?.trim()}>
              {weakness ? 'Update' : 'Add'} Weakness
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WeaknessModal;